import React from "react";
import type { Client, FollowUpStatusItem } from "../../../types";
import { displayPhoneNumber } from "../../../utils/phoneUtils";
import { maskCpfForDisplay } from "../../../utils/cpfUtils";
import { createDateInBrazil, isBirthdayToday } from "../../../utils/dateUtils";
import { DataList, ListColumn, ListAction, Avatar, Icons } from "../../shared";
import {
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";
import { CarExpiredFollowUpReminder } from "../AdminAppointments/CarExpiredFollowUpReminder";

interface ClientsListViewProps {
  clients: Client[];
  onEdit: (client: Client) => void;
  onDelete: (clientId: number) => void;
  onViewCars: (clientId: number) => void;
  onViewDetails: (clientId: number) => void;
  companyCategory?: "car" | "barbershop";
  expiredByClientId?: Map<number, FollowUpStatusItem[]>;
  onExpiredScheduled?: () => void;
}

export const ClientsListView: React.FC<ClientsListViewProps> = ({
  clients,
  onEdit,
  onDelete,
  onViewCars,
  onViewDetails,
  companyCategory,
  expiredByClientId,
  onExpiredScheduled,
}) => {
  const formatLastAppointment = (dateString?: string) => {
    if (!dateString) return "Nunca";
    try {
      const dateInBrazil = createDateInBrazil(dateString);
      return dateInBrazil.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const columns: ListColumn<Client>[] = [
    {
      key: "name",
      label: "Cliente",
      width: "3fr", // Give more space to name column
      render: (client) => (
        <div className="flex items-center">
          <Avatar text={client.name} size="md" variant="blue" />
          <div className="ml-3 flex items-center gap-2 min-w-0">
            <p
              className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors duration-200 truncate"
              onClick={() => onViewDetails(client.id)}
            >
              {client.name}
            </p>
            {isBirthdayToday(client.birthday) && (
              <span className="text-yellow-500 text-lg" title="Aniversário hoje!">
                🎂
              </span>
            )}
            <CarExpiredFollowUpReminder
              items={expiredByClientId?.get(client.id) ?? []}
              clientId={client.id}
              clientName={client.name}
              clientPhone={client.phoneNumber}
              onScheduled={onExpiredScheduled}
            />
          </div>
        </div>
      ),
    },
    {
      key: "phoneNumber",
      label: "Telefone",
      width: "2fr",
      render: (client) => (
        <span className="text-sm text-gray-900">
          {displayPhoneNumber(client.phoneNumber)}
        </span>
      ),
    },
    {
      key: "cpf",
      label: "CPF",
      width: "1.5fr",
      render: (client) =>
        client.cpf ? (
          <span
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 tracking-wide"
            title="CPF"
          >
            {maskCpfForDisplay(client.cpf)}
          </span>
        ) : (
          <span className="text-sm text-gray-600">—</span>
        ),
    },
    {
      key: "address",
      label: "Endereço",
      width: "2fr",
      render: (client) => (
        <span className="text-sm text-gray-600 truncate" title={client.address || undefined}>
          {client.address || "—"}
        </span>
      ),
    },
    {
      key: "totalAppointments",
      label: "Agendamentos",
      width: "1fr",
      className: "text-center",
      render: (client) => (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {client.appointmentCount}
        </span>
      ),
    },
    {
      key: "lastAppointment",
      label: "Último Agendamento",
      width: "2fr",
      className: "text-center",
      render: (client) => (
        <span className="text-sm text-gray-600">
          {formatLastAppointment(client.lastAppointment?.appointmentDate)}
        </span>
      ),
    },
  ];

  const actions: ListAction<Client>[] = [
    {
      label: "Enviar WhatsApp",
      icon: (
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
          <path d={WHATSAPP_ICON_PATH} />
        </svg>
      ) as any,
      onClick: (client) =>
        sendWhatsAppMessage({
          phoneNumber: client.phoneNumber,
          message: '',
        }),
      className:
        "text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/40",
      title: "Enviar mensagem via WhatsApp",
    },
    ...(companyCategory === "car"
      ? [
          {
            label: "Gerenciar veículos",
            icon: <Icons.Car sx={{ fontSize: 16 }} />,
            onClick: (client: Client) => onViewCars(client.id),
            className:
              "text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/40",
            title: "Gerenciar veículos",
          },
        ]
      : []),
    {
      label: "Editar cliente",
      icon: <Icons.Edit sx={{ fontSize: 16 }} />,
      onClick: (client) => onEdit(client),
      className:
        "text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40",
      title: "Editar cliente",
    },
    {
      label: "Excluir cliente",
      icon: <Icons.Delete sx={{ fontSize: 16 }} />,
      onClick: (client) => onDelete(client.id),
      className:
        "text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40",
      title: "Excluir cliente",
    },
  ];

  return (
    <>
      <DataList<Client>
        data={clients}
        columns={columns}
        itemLabel={clients.length === 1 ? "cliente" : "clientes"}
        actions={actions}
        keyExtractor={(client) => client.id}
        emptyMessage="Nenhum cliente encontrado"
      />
    </>
  );
};
